import React, { useCallback, useState, useEffect } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";

import { useStateSelector } from "utils/field";
import { getMemberOrgIds, getOrgName } from "utils/organization/membership";
import { addModelsToOrgFolderAPI, removeModelsFromOrgFolderAPI } from "utils/organization/api";
import { readApiResponse } from "utils/api";

import { actions as organizationActions } from "store/organizations";

import Button from "components/Button";
import Select from "components/Input/Select";
import Checkbox from "components/Input/Checkbox";
import Accordion from "components/Accordion";

import classes from "./style.module.scss";
import sharedClasses from "features/Model/sharedStyles.module.scss";

import ModelShare from "assets/images/icons/JSX/ModelShare";

const { fetchUserOrganization, fetchOrgDir } = organizationActions;

// Sharing a model means putting it in the organization's shared folder, the same move the
// dashboard makes. Everyone in the organization reads that one folder, so there is nothing to
// hand out per member: the model is either in the shared folder or in the owner's own directory.
const FileSharing = ({ fetchUserOrganization, fetchOrgDir }) => {
    const [permissionAcknowledged, setPermissionAcknowledged] = useState(false);
    const [sharingLoading, setSharingLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [chosenOrgId, setChosenOrgId] = useState("");

    const uid = useStateSelector(`user.uid`);
    const organization = useStateSelector(`organization`) || {};
    const { name: modelName = "" } = useStateSelector(`model.modelDetails`) || {};
    const modelId = useStateSelector(`model.modelId`) || "";

    const orgIds = getMemberOrgIds(organization);
    const orgId = chosenOrgId || orgIds[0] || "";
    const hasOrgDetails = Boolean(organization?.details?.[orgId]);

    // Depending on the whole organization object re-ran this on every change to that slice,
    // including the loading flag these fetches set first.
    useEffect(() => {
        if (!uid || !orgId) return;

        if (!hasOrgDetails) fetchUserOrganization(uid, orgId);

        //the shared tree says whether this model is already in the folder
        fetchOrgDir(orgId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uid, orgId, hasOrgDetails]);

    const isShared = Boolean(organization?.modelDir?.[orgId]?.models?.[modelId]);

    const handleShareClick = useCallback(async () => {
        setSharingLoading(true);
        setMessage("");

        const {
            status,
            data: { hasErrors = false } = {},
            message: errorMessage,
        } = readApiResponse(
            isShared
                ? await removeModelsFromOrgFolderAPI({ uid, orgId, modelIds: [modelId] })
                : await addModelsToOrgFolderAPI({ uid, orgId, modelIds: [modelId], parentFolderId: null })
        );

        await fetchOrgDir(orgId);

        setSharingLoading(false);
        setPermissionAcknowledged(false);

        if (status !== 200) {
            setMessage(errorMessage || "That did not work. Please try again.");

            return;
        }

        if (hasErrors) {
            setMessage(
                isShared
                    ? "Only the owner or an organization admin can take this model out of the shared folder."
                    : "You can only share a model you have write access to."
            );

            return;
        }

        setMessage(
            isShared
                ? "Removed from the shared folder. The model is back in its owner's directory."
                : "Added to the shared folder. Everyone in the organization can open it."
        );
    }, [isShared, uid, orgId, modelId, fetchOrgDir]);

    return (
        <div className={classes.accordionContainer}>
            <Accordion
                className={sharedClasses.accordion}
                heading={
                    <div className={sharedClasses.accordionIconHeading}>
                        Organizational File Sharing <ModelShare />
                    </div>
                }
                large
            >
                <div>
                    <div className={classes.orgHeader}>
                        {orgIds.length === 1 && (
                            <p>
                                Your organization: <strong>{getOrgName(organization, orgId)}</strong>
                            </p>
                        )}
                        <p>
                            File name: <strong>{modelName || "None"}</strong>
                        </p>
                    </div>
                    {orgIds.length > 1 && (
                        <Select
                            label="Choose the shared folder of the organization to add this file to:"
                            options={orgIds.map((id) => ({ label: getOrgName(organization, id), value: id }))}
                            input={{ value: orgId, onChange: (value) => setChosenOrgId(value) }}
                        />
                    )}
                    <p className={classes.helperText}>
                        {isShared
                            ? "This model is in the shared folder, where every member of the organization can open it."
                            : "Adding this model to the shared folder moves it out of your own directory and gives every member of the organization access to it."}
                    </p>
                    {!isShared && (
                        <div className={classes.sharingConfirmationText}>
                            <Checkbox
                                name="permissionAcknowledged"
                                label="I have recieved permission from all relevant parties to share this file and its content with my organization"
                                large
                                input={{
                                    value: permissionAcknowledged,
                                    onChange: () => setPermissionAcknowledged(!permissionAcknowledged),
                                }}
                            />
                        </div>
                    )}
                    <div className={classes.shareButtonContainer}>
                        <Button
                            className={classes.shareButton}
                            type={isShared ? "hollow" : "default"}
                            onClick={handleShareClick}
                            disabled={!orgId || !modelId || sharingLoading || (!isShared && !permissionAcknowledged)}
                        >
                            {sharingLoading
                                ? isShared
                                    ? "Removing..."
                                    : "Sharing..."
                                : isShared
                                  ? "Remove from Shared Folder"
                                  : "Add to Shared Folder"}
                        </Button>
                    </div>
                    {message && <p className={classes.resultMessage}>{message}</p>}
                </div>
            </Accordion>
        </div>
    );
};

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    fetchUserOrganization: (uid, orgId) => dispatch(fetchUserOrganization(uid, orgId)),
    fetchOrgDir: (orgId) => dispatch(fetchOrgDir(orgId, true)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FileSharing);
